/**
 * SuperAdminPage - Main super admin panel container
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Shield,
    LayoutDashboard,
    Building2,
    TrendingUp,
    Settings,
    Users,
    LogOut,
    Menu,
    X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import SuperAdminDashboard from '../components/superadmin/SuperAdminDashboard';
import EnhancedDashboard from '../components/superadmin/EnhancedDashboard';
import TenantManagement from '../components/superadmin/TenantManagement';
import PlatformSettings from '../components/superadmin/PlatformSettings';
import AdminManagement from '../components/superadmin/AdminManagement';

// Sidebar menu items - dynamic based on role
const getMenuItems = (isSuperAdmin) => {
    const items = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'tenants', label: 'Tenants', icon: Building2 },
        { id: 'usage', label: 'Usage & Billing', icon: TrendingUp },
        { id: 'settings', label: 'Settings', icon: Settings },
    ];

    // Add Admin Management only for super_admin
    if (isSuperAdmin) {
        items.push({ id: 'admins', label: 'Admin Management', icon: Users });
    }

    return items;
};

// Sidebar component - Icon-based like tenant admin
function Sidebar({ activeTab, onTabChange, user, onLogout, isSuperAdmin }) {
    const menuItems = getMenuItems(isSuperAdmin);
    return (
        <aside className="w-20 h-screen bg-gradient-to-b from-[var(--g6-secondary)] to-[#002A40] flex flex-col items-center py-6 shadow-2xl sticky top-0">
            {/* Logo */}
            <div className="mb-8 flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg overflow-hidden">
                    <img
                        src="/Logo.png"
                        alt="G6th Sense"
                        className="w-14 h-14 object-contain scale-110"
                    />
                </div>
            </div>

            {/* Navigation Icons */}
            <nav className="flex-1 flex flex-col gap-2 w-full px-2 overflow-y-auto no-scrollbar">
                {menuItems.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => onTabChange(item.id)}
                        className={`group relative p-4 rounded-xl transition-all duration-200 flex-shrink-0 ${
                            activeTab === item.id
                                ? "bg-white/20 shadow-lg"
                                : "hover:bg-white/10"
                        }`}
                        title={item.label}
                    >
                        <item.icon className={`w-6 h-6 mx-auto ${
                            activeTab === item.id ? "text-white" : "text-gray-300"
                        }`} />
                        <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                            {item.label}
                        </div>
                    </button>
                ))}
            </nav>

            {/* User Profile & Logout - Fixed at bottom */}
            <div className="flex-shrink-0 pt-4 border-t border-white/10 w-full px-2">
                <div className="group relative p-3 rounded-xl bg-white/10 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--g6-primary)] to-[var(--g6-primary-dark)] flex items-center justify-center mx-auto text-white font-bold">
                        {user?.username?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                    <div className="absolute left-full ml-4 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                        <div className="font-medium">{user?.username || 'Super Admin'}</div>
                        <div className="text-xs text-gray-400">
                            {user?.role === 'super_admin' ? 'Super Administrator' : 'Sub Administrator'}
                        </div>
                    </div>
                </div>

                <button
                    onClick={onLogout}
                    className="group relative p-3 rounded-xl hover:bg-red-500/20 transition-all duration-200 w-full"
                    title="Logout"
                >
                    <LogOut className="w-6 h-6 mx-auto text-gray-300 group-hover:text-white" />
                    <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                        Logout
                    </div>
                </button>
            </div>
        </aside>
    );
}

// Placeholder components
function TenantsPlaceholder() {
    return (
        <div className="p-6">
            <div className="bg-white rounded-xl border p-8 text-center">
                <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h2 className="text-xl font-semibold">Tenant Management</h2>
                <p className="text-gray-600 mt-2">
                    Tenant management interface coming soon
                </p>
            </div>
        </div>
    );
}

function UsagePlaceholder() {
    return (
        <div className="p-6">
            <div className="bg-white rounded-xl border p-8 text-center">
                <TrendingUp className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h2 className="text-xl font-semibold">Usage & Billing</h2>
                <p className="text-gray-600 mt-2">
                    Usage tracking and billing management coming soon
                </p>
            </div>
        </div>
    );
}

function SettingsPlaceholder() {
    return (
        <div className="p-6">
            <div className="bg-white rounded-xl border p-8 text-center">
                <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h2 className="text-xl font-semibold">Platform Settings</h2>
                <p className="text-gray-600 mt-2">
                    Platform configuration coming soon
                </p>
            </div>
        </div>
    );
}

function SuperAdminPage() {
    const navigate = useNavigate();
    const { isAuthenticated, user, logout } = useAuth();

    const [activeTab, setActiveTab] = useState('dashboard');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Check if user is super admin
    const isSuperAdmin = user?.role === 'super_admin';

    // Set document title for super admin panel
    useEffect(() => {
        document.title = 'G6th Sense Super Admin Panel';
        return () => {
            document.title = 'G6th Sense Admin Panel';
        };
    }, []);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/super-admin/login', { replace: true });
        } else if (!isSuperAdmin && user?.role !== 'sub_admin') {
            // Only redirect if not super_admin AND not sub_admin
            navigate('/admin', { replace: true });
        }
    }, [isAuthenticated, isSuperAdmin, user, navigate]);

    // Handle logout
    const handleLogout = () => {
        logout();
        navigate('/super-admin/login');
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setMobileMenuOpen(false);
    };

    // Render content
    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return <EnhancedDashboard onNavigate={handleTabChange} />;
            case 'tenants':
                return <TenantManagement />;
            case 'usage':
                return <UsagePlaceholder />;
            case 'settings':
                return <PlatformSettings />;
            case 'admins':
                return <AdminManagement />;
            default:
                return <EnhancedDashboard onNavigate={handleTabChange} />;
        }
    };

    // Don't render anything while redirecting
    if (!isAuthenticated || (!isSuperAdmin && user?.role !== 'sub_admin')) {
        return null;
    }

    return (
        <div className="h-screen flex bg-gray-50">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block">
                <Sidebar
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    user={user}
                    onLogout={handleLogout}
                    isSuperAdmin={isSuperAdmin}
                />
            </div>

            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-gradient-to-r from-[var(--g6-secondary)] to-[#002A40] text-white px-4 py-3 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-2">
                    <img src="/Logo.png" alt="G6" className="h-8 w-8" />
                    <span className="font-bold">Super Admin</span>
                </div>
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                    {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {/* Mobile Sidebar Overlay */}
            {mobileMenuOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
                        <Sidebar
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                            user={user}
                            onLogout={handleLogout}
                            isSuperAdmin={isSuperAdmin}
                        />
                    </div>
                </>
            )}

            {/* Main Content */}
            <div className="flex-1 overflow-auto">
                <main className="h-full pt-14 lg:pt-0">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
}

export { SuperAdminPage };
export default SuperAdminPage;
